// Pure helpers of the Trigger tab (no API, no React): the condition tree (immutable updates by index path),
// the editor's form values and the payload.

export const isGroup = (node) => Boolean(node && (Array.isArray(node.all) || Array.isArray(node.any)));
export const groupMode = (group) => (Array.isArray(group.all) ? "all" : "any");
export const childrenOf = (group) => (Array.isArray(group.all) ? group.all : group.any) ?? [];
export const withChildren = (mode, children) => (mode === "all" ? { all: children } : { any: children });
export const emptyLeaf = () => ({ field: "", op: "" });
export const operandMode = (leaf) => (leaf.param ? "param" : leaf.context ? "context" : "value");

/** The starting value of a leaf for an operator's value kind. */
export function freshOperand(kind) {
  if (kind === "list") return { value: [] };
  if (kind === "pair") return { value: ["", ""] };
  if (kind === "amount") return { value: null, unit: "DAY" };
  if (kind === "none") return {};
  return { value: "" };
}

/** Replaces the node at `path` (indices into children from the root group). */
export function updateAt(root, path, fn) {
  if (!path.length) return fn(root);
  if (!isGroup(root)) return root;
  const [head, ...rest] = path;
  return withChildren(groupMode(root), childrenOf(root).map((child, index) => (index === head ? updateAt(child, rest, fn) : child)));
}

/** Removes the node at `path`; a group left empty goes too (the root becomes null). */
export function removeAt(root, path) {
  if (!path.length) return null;
  if (!isGroup(root)) return root;
  const [head, ...rest] = path;
  const kids = childrenOf(root)
    .map((child, index) => (index !== head ? child : rest.length ? removeAt(child, rest) : null))
    .filter(Boolean);
  return kids.length ? withChildren(groupMode(root), kids) : null;
}

export function countLeaves(node) {
  if (!node) return 0;
  return isGroup(node) ? childrenOf(node).reduce((sum, child) => sum + countLeaves(child), 0) : 1;
}

/** Operators that accept a field's data type. */
export const operatorsFor = (operators, dataType) => (operators ?? []).filter((op) => (op.types ?? []).includes(dataType));

/** Drops half-filled pieces the server would refuse outright: leaves without a field / operator, empty groups. */
export function cleanTree(node) {
  if (!node) return null;
  if (!isGroup(node)) return node.field && node.op ? node : null;
  const kids = childrenOf(node).map(cleanTree).filter(Boolean);
  return kids.length ? withChildren(groupMode(node), kids) : null;
}

/** A one-line reading of a condition tree ("Every record" for none). */
export function describeTree(node, labelOf = (op) => op) {
  if (!node) return "Every record";
  if (isGroup(node)) {
    const parts = childrenOf(node).map((child) => describeTree(child, labelOf));
    return parts.length > 1 ? `(${parts.join(groupMode(node) === "all" ? " AND " : " OR ")})` : parts[0] ?? "";
  }
  const operand = node.param ? `{param ${node.param}}` : node.context ? `{${node.context}}` : Array.isArray(node.value) ? `[${node.value.join(", ")}]` : node.value ?? "";
  return `${node.field} ${labelOf(node.op)} ${operand}${node.unit ? ` ${node.unit.toLowerCase()}` : ""}`.trim();
}

/** Editor values of a saved trigger (or a new one). Params keep their default as text while edited. */
export const toFormValues = (record) => ({
  code: record?.code ?? "",
  name: record?.name ?? "",
  description: record?.description ?? "",
  category: record?.category ?? "",
  trigger_type: record?.trigger_type ?? "CONDITION",
  source_fid: record?.source_fid ?? null,
  domain_event_fid: record?.domain_event_fid ?? null,
  module_key: record?.module_key ?? "",
  anchor_field_key: record?.anchor_field_key ?? "",
  anchor_recurrence: record?.anchor_recurrence ?? "ONCE",
  conditions: record?.conditions ?? null,
  params: (record?.params ?? []).map((p) => ({ key: p.key, label: p.label === p.key ? "" : p.label ?? "", data_type: p.data_type, required: p.required === true, default: p.default === undefined || p.default === null ? "" : String(p.default) })),
  tags: record?.tags ?? [],
  is_activated: record ? record.is_activated !== false : true,
});

/** Param definitions as the condition builder reads them (rows with a key). */
export const paramDefsOf = (params) => (params ?? []).filter((p) => p.key).map((p) => ({ key: p.key, label: p.label || p.key, data_type: p.data_type }));

/** The payload of a definition: what another type doesn't use is sent as null. */
export function toPayload(values) {
  const type = values.trigger_type;
  const broadcast = type === "BROADCAST";
  return {
    code: values.code.trim().toUpperCase(),
    name: values.name.trim(),
    description: values.description.trim() || null,
    category: values.category.trim() || null,
    trigger_type: type,
    source_fid: broadcast ? null : values.source_fid,
    domain_event_fid: type === "DOMAIN_EVENT" ? values.domain_event_fid : null,
    module_key: broadcast ? values.module_key || null : null,
    conditions: broadcast ? null : cleanTree(values.conditions),
    params: values.params.map((p) => ({ key: p.key, label: p.label.trim() || p.key, data_type: p.data_type, required: p.required, ...(p.default !== "" && { default: p.default }) })),
    anchor_field_key: type === "DATE_ANCHOR" ? values.anchor_field_key || null : null,
    anchor_recurrence: type === "DATE_ANCHOR" ? values.anchor_recurrence : null,
    tags: values.tags,
    is_activated: values.is_activated,
  };
}
